// ARIA's word for how urgently a live region is read: "polite" waits until the screen
// reader has finished its current sentence, "assertive" interrupts it. Errors are
// assertive, everything else is polite.
export type Politeness = 'polite' | 'assertive'

export interface AnnounceOptions {
  assertive?: boolean
  // Set by value-driven callers (useAnnouncement) so a changed value replaces the message it
  // announced a moment ago instead of being read after it: "4 projects", not "12 projects.
  // 4 projects". One-off calls from event handlers leave it unset and always append.
  source?: string
}

export type Announce = (text: string, options?: AnnounceOptions) => void

export interface Announcement {
  text: string
  politeness: Politeness
  // Counts announcements. Announcer uses it to alternate between its two regions and to
  // tell whether a message arrived after it mounted.
  sequence: number
  // When it was made, so a message arriving right behind it can be merged in.
  at: number
  // Who spoke the last piece of `text`, and the text before that piece, so the same source
  // can replace only its own part of a merged sentence.
  source?: string
  head: string
}

export const EMPTY_ANNOUNCEMENT: Announcement = {
  text: '',
  politeness: 'polite',
  sequence: 0,
  at: 0,
  head: '',
}

// VoiceOver reads a region when it gets round to it, not when it changed. A second message
// inside this window would empty the first one's region before it was spoken (seen on
// /analytics: the summary was lost behind the user count), so the two become one sentence.
export const MERGE_WINDOW_MS = 1000

function joinSentences(previous: string, next: string) {
  return `${previous.replace(/[.!?]$/, '')}. ${next}`
}

export function nextAnnouncement(
  previous: Announcement,
  text: string,
  politeness: Politeness,
  now: number,
  source?: string
): Announcement {
  const next = { politeness, sequence: previous.sequence + 1, at: now, source }
  const merge =
    previous.text !== '' &&
    previous.politeness === politeness &&
    now - previous.at < MERGE_WINDOW_MS
  if (!merge) return { ...next, text, head: '' }

  const replacesOwnPiece = source !== undefined && source === previous.source
  const head = replacesOwnPiece ? previous.head : previous.text
  return { ...next, text: head ? joinSentences(head, text) : text, head }
}
